import { Alert, Box, DialogContent } from '@mui/material';
import React, { ReactNode, useEffect } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useCollections, useMetaData } from '../../api';
import CollectionField from '../../forms/collection-field/CollectionField';
import DescriptionField from '../../forms/description-field/DescriptionField';
import ExerciseField from '../../forms/exercise-field/ExerciseField';
import LayoutField from '../../forms/layout-field/LayoutField';
import ModuleField from '../../forms/module-filed/ModuleField';
import SimulatorField from '../../forms/simulator-field/SimulatorField';
import TitleField from '../../forms/title-field/TitleField';
import KSimHeader from '../../k-sim-header/KSimHeader';
import { ExerciseFile, ExerciseMetaData, ExerciseType, Simulator, TechnologyPlatform } from '../../types';
import { Collection } from '../../types/collection';
import { Module } from '../../types/module';
import StudentPositions from '../student-positions/StudentPositions';
import { AdHocExerciseFormData } from './CreateAdHocExerciseDialog';

export type ExerciseStepProps = UseFormReturn<AdHocExerciseFormData> & {
  adHoc?: boolean;
  actions: ReactNode;
  handleNext: SubmitHandler<AdHocExerciseFormData>;
  simulators?: Simulator[];
  stepper?: ReactNode;
};

const ExerciseStep = ({
  adHoc,
  actions,
  control,
  clearErrors,
  handleNext,
  handleSubmit,
  simulators,
  stepper,
  setValue,
  watch,
}: ExerciseStepProps) => {
  const [exerciseId, exercises, exercisesMetaData, simulatorId, collectionId, moduleId, type, vessel] = watch([
    'exerciseId',
    'exercises',
    'exercisesMetaData',
    'simulator',
    'collectionId',
    'moduleId',
    'type',
    'vessel',
  ]);

  const { collections, isCollectionsError } = useCollections(simulatorId);

  useEffect(() => {
    if (collections) {
      setValue(
        'exercises',
        collections
          ?.map((collection: Collection) => collection.modules)
          .flat()
          .map((module: Module) => module.exercises)
          .flat(),
      );
    }
  }, [collections]);

  const simulatorType = simulators?.find(simulator => simulator.id === simulatorId)?.type;
  const simulatorPlatform = simulators?.find(simulator => simulator.id === watch('simulator'))?.platform;
  const isSelfStudy = type === ExerciseType.SelfStudy;

  const { isLoading, isExerciseMetaDataError, getMetaData } = useMetaData((exercisesMetaData: ExerciseMetaData[]) => {
    setValue('exercisesMetaData', exercisesMetaData);
  });

  useEffect(() => {
    setValue('exercisesIds', null);
    setValue('exerciseId', null);
    setValue('exercisesMetaData', null);
    setValue('vessel', null);
    setValue('layout', '');
  }, [type]);

  useEffect(() => {
    setValue('exerciseId', null);
  }, [simulatorId]);

  useEffect(() => {
    setValue('exerciseId', null);
  }, [collectionId, moduleId]);

  useEffect(() => {
    setValue('exercisesMetaData', []);
    setValue('vessel', null);
    setValue('studentEnvironment', null);

    if (isSelfStudy && simulatorPlatform === TechnologyPlatform.Spirit && exerciseId) {
      getMetaData([exerciseId]);
    }
  }, [exerciseId]);

  const modules = collections?.find(collection => collection.id === collectionId)?.modules;
  const moduleExercises = modules?.find(module => module.id === moduleId)?.exercises;
  const selectedExercise = exercises?.find((exercise: ExerciseFile) => exercise.id === exerciseId);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleNext)}
      noValidate
      autoComplete="off"
      sx={{ display: 'contents' }}
    >
      <DialogContent>
        {stepper}
        <Box display="flex" flexDirection="column" gap={3}>
          <KSimHeader adHoc exerciseType={type} mobileOnly simulatorType={simulatorType} sx={{ height: '153px' }} />

          {type === ExerciseType.Classroom && !adHoc ? (
            <>
              <TitleField control={control} name="title" />
              <DescriptionField control={control} name="description" />
            </>
          ) : null}

          <SimulatorField control={control} name="simulator" simulators={simulators ?? []} exerciseType={type} />

          <CollectionField
            control={control}
            collections={collections}
            loading={!!simulatorId && !collections}
            name="collectionId"
          />

          <ModuleField control={control} modules={modules} name="moduleId" />

          <ExerciseField
            control={control}
            disabled={!exercises || !simulatorId}
            exercises={moduleExercises || exercises}
            loading={isLoading}
            name="exerciseId"
          />

          {type === ExerciseType.SelfStudy && (
            <>
              {exercisesMetaData?.length === 1 && exercisesMetaData[0]?.metaData.length ? (
                <>
                  {simulatorPlatform === TechnologyPlatform.Spirit ? (
                    <StudentPositions
                      control={control}
                      exerciseId={exercisesMetaData[0].id}
                      studentEnvironment={exercisesMetaData[0].metaData}
                      setValue={setValue}
                      vessel={vessel}
                      clearErrors={clearErrors}
                    />
                  ) : null}

                  {selectedExercise?.layouts?.length ? (
                    <LayoutField control={control} layouts={selectedExercise.layouts} name="layout" />
                  ) : null}
                </>
              ) : null}
            </>
          )}
          {isCollectionsError && <Alert severity="error">An issue prevented the list of exercises to be fetched</Alert>}
          {isExerciseMetaDataError && (
            <Alert severity="error">
              Something went wrong and we couldn&lsquo;t load your student positions. Please try again
            </Alert>
          )}
        </Box>
      </DialogContent>
      {actions}
    </Box>
  );
};

export default ExerciseStep;
